import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Header } from "@/components/site/Header";
import { ReadingProgressBar } from "@/components/site/ReadingProgressBar";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---------------- Table of contents ---------------- */

const TOC = [
  { id: "distinct-layers", title: (<>Financial Infrastructure Is Made{" "}<br className="hidden lg:block" />of Several Distinct Layers</>) },
  { id: "swift-at-scale", title: "SWIFT Is a Useful Example of Infrastructure at Scale" },
  { id: "layers-keep-moving", title: "The Layers Keep Moving" },
  { id: "framework-good-for", title: (<>What This Framework Is Actually{" "}<br className="hidden lg:block" />Good For</>) },
];

/* ---------------- Shared typographic atoms ---------------- */

const P = ({ children, className = "" }) => (
  <p className={`mt-5 text-base leading-[1.75] text-muted-foreground ${className}`}>{children}</p>
);

const Note = ({ children, testId }) => (
  <div
    className="mt-6 rounded-xl border-l-[3px] border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/[0.05] px-6 py-5"
    data-testid={testId}
  >
    <p className="text-[0.95rem] leading-relaxed text-foreground/80">{children}</p>
  </div>
);

const Section = ({ id, index, title, children }) => (
  <section id={id} className="scroll-mt-28 pt-14 first:pt-0" data-testid={`article-section-${id}`}>
    <div className="flex items-baseline gap-4">
      <span className="text-sm font-medium tracking-[0.08em] text-[hsl(174_86%_24%)]">
        {String(index).padStart(2, "0")}
      </span>
      <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.7rem]">
        {title}
      </h2>
    </div>
    <div className="mt-1 border-t border-[hsl(var(--primary))]/10 pt-1">{children}</div>
  </section>
);

/* ---------------- Hero ---------------- */

const ArticleHero = () => (
  <section data-testid="article-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="article-breadcrumb">
        <Link
          to="/resources"
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid="article-breadcrumb-resources-link"
        >
          Resources
        </Link>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        <span className="text-foreground/80">Understanding Modern Financial Infrastructure</span>
      </nav>

      {/* Hero panel */}
      <div
        className="mt-10 rounded-3xl bg-[hsl(var(--primary))]/[0.05] px-[14px] py-[33px] sm:px-[38px] sm:py-[49px] lg:px-[54px] lg:py-[65px]"
        data-testid="article-hero-panel"
      >
        <h1 className="font-serif-editorial max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Understanding Modern Financial Infrastructure
        </h1>
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="flex items-center gap-2.5">
            <span className="h-[3px] w-5 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(174_86%_24%)]">
              Article
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            4 min read
          </span>
        </div>
        <div className="mt-8 max-w-4xl lg:max-w-[1080px]">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            Most people only notice financial infrastructure when it fails: a
            wire transfer that takes three days longer than expected, a payment
            that arrives with no explanation of what it was for, a
            reconciliation that doesn't quite add up. The rest of the time, it
            works quietly in the background, moving trillions of dollars a day
            without anyone giving it much thought. Understanding what that
            background actually consists of makes it much easier to evaluate
            any new payment technology that comes along.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="article-hero-tags">
          {["Financial Infrastructure", "Payments"].map((t) => (
            <span
              key={t}
              className="rounded-full border border-[hsl(var(--primary))]/25 px-4 py-1.5 text-sm font-medium text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Sticky table of contents with scrollspy ---------------- */

const TocSidebar = () => {
  const [activeId, setActiveId] = useState(TOC[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current = TOC[0].id;
      for (const item of TOC) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <aside className="hidden lg:block">
      <nav
        className="sticky top-24 rounded-2xl bg-card/80 p-7 px-[23px] shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px]"
        aria-label="Table of contents"
        data-testid="article-toc"
      >
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Table of Contents
        </h2>
        <ol className="mt-5 space-y-1">
          {TOC.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={`block rounded-md py-1.5 pr-2 text-sm leading-snug transition-colors hover:text-[hsl(174_86%_24%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  activeId === s.id
                    ? "font-medium text-[hsl(174_86%_24%)]"
                    : "text-muted-foreground"
                }`}
                data-testid={`article-toc-link-${s.id}`}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  );
};

/* ---------------- Article body ---------------- */

const LAYERS = [
  {
    name: "Rails",
    text: "Rails are the actual pathways money travels on. A wire, an ACH transfer, a card network, and a blockchain-based transfer are all different rails, each with its own speed, cost, and reach. Examples include ACH in the United States, SEPA in Europe, Faster Payments in the UK, card networks such as Visa and Mastercard, SWIFT-supported bank transfers, and blockchain networks.",
  },
  {
    name: "Messaging",
    text: "Messaging is the information that travels alongside the money, describing who is paying whom, for what, and under what reference. This is a separate concern from the rail itself; two payments can move on the same rail while carrying very different amounts of usable detail. Today, many financial institutions are moving to ISO 20022, a global messaging standard that carries significantly richer payment information than older formats.",
  },
  {
    name: "Settlement",
    text: "Settlement is the point at which a payment becomes final and irreversible. A card payment might authorize instantly, with settlement following a day or two later; a wire, by contrast, often settles the same day it's sent. This gap between authorization and settlement is where a lot of financial risk and reconciliation work actually lives.",
  },
  {
    name: "Compliance and Oversight",
    text: "Compliance and oversight sit underneath all of it. Licensing requirements, anti-money-laundering checks, and identity verification apply regardless of which rail a payment uses, and they're often what determines whether a new payment method is viable for a business at all.",
  },
];

const ArticleBody = () => (
  <section data-testid="article-body-section">
    <Container>
      <div className="grid gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">
        <TocSidebar />

        <article className="max-w-[76ch]" data-testid="article-content">
          {/* 01 — Distinct Layers */}
          <Section id="distinct-layers" index={1} title={<>Financial Infrastructure Is Made of Several{" "}<br className="hidden lg:block" />Distinct Layers</>}>
            <P>
              It helps to separate financial infrastructure into a few layers
              that often get bundled together in conversation, because a new
              technology can change one of these layers without touching the
              others.
            </P>
            <div className="mt-9 space-y-9" data-testid="article-layers-blocks">
              {LAYERS.map((l) => (
                <div key={l.name} className="border-t border-[hsl(var(--accent))]/70 pt-6">
                  <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground">
                    {l.name}
                  </h3>
                  <p className="mt-3.5 text-base leading-[1.75] text-muted-foreground">{l.text}</p>
                </div>
              ))}
            </div>
            <Note testId="article-note-viability">
              Even the fastest payment rail cannot be used if a business cannot
              identify the recipient, satisfy AML requirements, or meet local
              regulatory obligations.
            </Note>
          </Section>

          {/* 02 — SWIFT at Scale */}
          <Section id="swift-at-scale" index={2} title="SWIFT Is a Useful Example of Infrastructure at Scale">
            <P>
              SWIFT is worth looking at closely because it shows what mature
              financial infrastructure looks like once it operates at global
              scale. The network connects more than 11,500 financial
              institutions across over 200 countries, and value equivalent to
              the entire world's GDP passes across it roughly every three days,
              according to SWIFT's own figures. As of 2026, 90% of cross-border
              payments sent through the network reach the beneficiary bank
              within an hour, already ahead of a G20 target originally set for
              2027.
            </P>
            <P>
              What makes SWIFT a useful illustration of the layered model above
              is that it operates as a messaging network. It carries the
              instructions and information that tell banks how to move money on
              their own rails, without moving the money itself.
            </P>
            <Note testId="article-note-which-layer">
              That's a small distinction, but a useful one to hold onto. When a
              new payment technology shows up for evaluation, it usually helps
              to work out which layer it's actually touching: a new rail, a
              richer messaging standard, or a faster settlement mechanism each
              solve a different kind of problem.
            </Note>
          </Section>

          {/* 03 — The Layers Keep Moving */}
          <Section id="layers-keep-moving" index={3} title="The Layers Keep Moving">
            <P>
              None of these layers stay still for long. Real-time payment rails
              are replacing overnight batch processing in country after
              country. Messaging is being standardized worldwide as SWIFT and
              other networks move to ISO 20022, carrying far richer transaction
              data than the free-text formats used for decades. Settlement
              itself is diversifying, with stablecoin-based systems now
              offering another route for moving value across borders, built on
              infrastructure separate from the traditional banking system.
            </P>
            <P>
              Even SWIFT is experimenting with a parallel blockchain-based
              ledger for tokenized settlement, alongside a retail payments
              framework rolling out across major remittance corridors in 2026.
              The traditional and the new increasingly sit side by side, each
              handling the scenarios it's best suited for, rather than one
              simply displacing the other.
            </P>
            <div className="mt-6 overflow-hidden rounded-xl ring-1 ring-[hsl(var(--primary))]/12" data-testid="article-layers-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-[hsl(var(--primary))]/12 bg-[hsl(var(--primary))]/[0.05] hover:bg-[hsl(var(--primary))]/[0.05]">
                    <TableHead className="h-12 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Layer
                    </TableHead>
                    <TableHead className="h-12 border-l border-[hsl(var(--primary))]/10 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Traditional Example
                    </TableHead>
                    <TableHead className="h-12 border-l border-[hsl(var(--primary))]/10 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Modern Example
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ["Rail", "ACH", "Blockchain"],
                    ["Messaging", "MT messages", "ISO 20022"],
                    ["Settlement", "T+1/T+2", "Near real-time"],
                    ["Money", "Bank deposits", "Stablecoins / CBDCs"],
                  ].map(([layer, traditional, modern]) => (
                    <TableRow key={layer} className="border-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/[0.02]">
                      <TableCell className="whitespace-normal px-5 py-4 align-top text-sm font-medium leading-relaxed text-foreground">
                        {layer}
                      </TableCell>
                      <TableCell className="whitespace-normal border-l border-[hsl(var(--primary))]/10 px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                        {traditional}
                      </TableCell>
                      <TableCell className="whitespace-normal border-l border-[hsl(var(--primary))]/10 px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                        {modern}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Section>

          {/* 04 — What This Framework Is Actually Good For */}
          <Section id="framework-good-for" index={4} title="What This Framework Is Actually Good For">
            <P>
              The value of thinking in layers isn't academic. When a new
              payment option comes up for evaluation, whether it's a faster
              domestic rail, a stablecoin settlement provider, or a new
              invoicing platform, the first useful question is which layer it
              changes.
            </P>
            <P>
              A faster rail changes how quickly money arrives, but says little
              about how much usable data comes with it. A better messaging
              standard improves that data without changing when a payment is
              truly final. A new settlement mechanism might solve a genuine
              cross-border cost problem while introducing its own questions
              around custody, recordkeeping, and compliance.
            </P>
            <Note testId="article-note-right-questions">
              Financial infrastructure will keep evolving, and most businesses
              can adopt new developments at their own pace rather than all at
              once. A finance team that can place a new payment technology
              within this layered structure is simply better equipped to ask
              the right questions before adopting it.
            </Note>
          </Section>
        </article>
      </div>
    </Container>
  </section>
);

/* ---------------- Final CTA ---------------- */

const ArticleFinalCta = () => (
  <section data-testid="article-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Reading the System Behind the Payment
          </h2>
          <div className="mx-auto max-w-2xl lg:max-w-[880px]">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              This article covered the layers that make up financial
              infrastructure and how they're evolving. WFII programs go a step
              further, helping finance professionals evaluate new payment
              technologies and understand how they fit into accounting,
              reporting, and compliance workflows.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="article-final-cta-explore-programs-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Page ---------------- */

export default function ArticleInfrastructurePage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <ReadingProgressBar />
        <main className="wfii-longform flex-1 space-y-[60px] lg:space-y-[100px]">
          <ArticleHero />
          <ArticleBody />
          <ArticleFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
